// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/COGS.tsx
// 
// تغییرات این نسخه:
// - 🆕 فیلد شماره موتر (truck_plate) در فرم ثبت و ویرایش
// - 🆕 نمایش شماره موتر کنار سریال در لیست
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI, warehouseAPI, customersAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian, gregorianToJalaliStr, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Edit2 } from "lucide-react";
import JournalFilter, { emptyFilter, applyFilter, JournalFilterValues, exportToExcel } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

export default function COGS() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [list, setList]         = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [showCats, setShowCats] = useState(false);
  const [showPay, setShowPay]   = useState<any>(null);
  const [cats, setCats]         = useState<any[]>([]);
  const [productCats, setProductCats] = useState<any[]>([]);
  const [productItems, setProductItems] = useState<any[]>([]);
  const [cashAccs, setCashAccs] = useState<any[]>([]);
  const [filter, setFilter]     = useState<JournalFilterValues>(emptyFilter);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [tempCats, setTempCats] = useState<string[]>([]);
  const [showCatFilter, setShowCatFilter] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState<any>(null);
  const [showBulkSaleModal, setShowBulkSaleModal] = useState(false);
  const [bulkSaleCustomerId, setBulkSaleCustomerId] = useState("");
  const [bulkSaleLoading, setBulkSaleLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [saleLoading, setSaleLoading] = useState(false);
  const [toast, setToast]       = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all"|"pending"|"paid">("all");

  const [form, setForm] = useState({
    date: todayGregorian(), recipient_name: "",
    category_id: "", product_category_id: "", product_item_id: "", product_item_type: "",
    currency: "USD", amount: "", exchange_rate: "1",
    status: "pending", account_id: "", account_name: "",
    notes: "", attachment: "", truck_plate: "", waybill_number: "", // 🆕
  });

  const [catForm, setCatForm] = useState({ name: "", parent_id: "", editId: "" });
  const [payForm, setPayForm] = useState({ account_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const [c, ec, pc, cust, a, pr, sv] = await Promise.all([
      journalAPI.listCogs(),
      journalAPI.getExpenseCats(),
      journalAPI.getProductCats(),
      customersAPI.list(),
      accountsAPI.getCashAccounts(),
      warehouseAPI.productsList(),
      warehouseAPI.servicesList(),
    ]);
    if (c.success)    setList(c.cogs || []);
    if (ec.success)   setCats(ec.categories || []);
    if (pc.success)   setProductCats(pc.categories || []);
    if (cust.success) setCustomers(cust.customers || []);
    if (a.success)    setCashAccs(a.accounts || []);
    const items: any[] = [];
    if (pr.success) (pr.products || []).forEach((p: any) => items.push({ value: `prd_${p.id}`, label: `📦 ${p.name}`, type: "product", id: p.id }));
    if (sv.success) (sv.services || []).forEach((s: any) => items.push({ value: `svc_${s.id}`, label: `🔧 ${s.name}`, type: "service", id: s.id }));
    setProductItems(items);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const catOptions = (list: any[]) => {
    const result: { value: string; label: string }[] = [{ value: "", label: "— بدون دسته‌بندی —" }];
    list.filter((c: any) => !c.parent_id).forEach((p: any) => {
      result.push({ value: String(p.id), label: p.name });
      list.filter((c: any) => c.parent_id === p.id).forEach((ch: any) => {
        result.push({ value: String(ch.id), label: `  └ ${ch.name}` });
      });
    });
    return result;
  };

  const buildSubmitPayload = (statusValue: "pending"|"paid") => {
    const cat     = cats.find((c: any) => String(c.id) === form.category_id);
    const pCat    = productCats.find((c: any) => String(c.id) === form.product_category_id);
    const account = cashAccs.find((a: any) => String(a.id) === form.account_id);
    const selItem = productItems.find(i => i.value === form.product_item_id);
    return {
      date: form.date, recipient_name: form.recipient_name,
      category_id: form.category_id ? parseInt(form.category_id) : null,
      category_name: cat?.name || "",
      product_category_id: form.product_category_id ? parseInt(form.product_category_id) : null,
      product_category_name: pCat?.name || "",
      product_item_id: selItem ? selItem.id : null,
      product_item_type: selItem ? selItem.type : null,
      currency: form.currency, amount: parseFloat(form.amount),
      exchange_rate: parseFloat(form.exchange_rate || "1"),
      status: statusValue,
      account_id: form.account_id ? parseInt(form.account_id) : null,
      account_name: account?.name || "",
      notes: form.notes, attachment: form.attachment,
      truck_plate: form.truck_plate, // 🆕
      waybill_number: form.waybill_number, // 🆕
    };
  };

  const validateForm = (statusValue: "pending"|"paid") => {
    if (!form.recipient_name) { showToast("نام دریافت‌کننده الزامی است", false); return false; }
    if (!form.amount)         { showToast("مبلغ الزامی است", false); return false; }
    if (statusValue === "paid" && !form.account_id) { showToast("حساب پرداختی برای ثبت نهایی الزامی است", false); return false; }
    return true;
  };

  const resetForm = () => setForm({ date: todayGregorian(), recipient_name: "", category_id: "", product_category_id: "", product_item_id: "", product_item_type: "", currency: "USD", amount: "", exchange_rate: "1", status: "pending", account_id: "", account_name: "", notes: "", attachment: "", truck_plate: "", waybill_number: "" });

  const handleSubmit = async () => {
    if (!validateForm("paid")) return;
    const res = await journalAPI.addCogs(buildSubmitPayload("paid"));
    if (res.success) {
      showToast(`COGS ${res.serial} ثبت شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleSubmitPending = async () => {
    if (!validateForm("pending")) return;
    const res = await journalAPI.addCogs(buildSubmitPayload("pending"));
    if (res.success) {
      showToast(`COGS ${res.serial} در انتظار تأیید ذخیره شد`);
      setShowForm(false); resetForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (item: any) => {
    if (!item.account_id) {
      setShowPay(item);
      return;
    }
    if (!window.confirm(`تأیید نهایی COGS ${item.serial}؟\nمبلغ از حساب کم می‌شود.`)) return;
    const res = await journalAPI.confirmCogs(item.id);
    if (res.success) { showToast("COGS تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن COGS ${serial} به حالت در انتظار؟\nمبلغ به حساب برگردانده می‌شود.`)) return;
    const res = await journalAPI.revertCogs(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const openEdit = (e: any) => setEditItem({
    id: e.id, date: e.date, recipient_name: e.recipient_name,
    category_id: e.category_id ? String(e.category_id) : "",
    product_category_id: e.product_category_id ? String(e.product_category_id) : "",
    product_item_id: e.product_item_type === "service" ? `svc_${e.product_item_id}` : e.product_item_id ? `prd_${e.product_item_id}` : "",
    product_item_type: e.product_item_type || "",
    currency: e.currency, amount: String(e.amount),
    exchange_rate: String(e.exchange_rate || "1"),
    status: e.status,
    account_id: e.account_id ? String(e.account_id) : "",
    account_name: e.account_name || "",
    notes: e.notes || "", attachment: e.attachment || "",
    truck_plate: e.truck_plate || "", // 🆕
    waybill_number: e.waybill_number || "", // 🆕
  });

  const handleEdit = async () => {
    if (!editItem.recipient_name) { showToast("نام دریافت‌کننده الزامی است", false); return; }
    if (!editItem.amount)         { showToast("مبلغ الزامی است", false); return; }
    if (editItem.status === "paid" && !editItem.account_id) { showToast("حساب پرداختی الزامی است", false); return; }
    const cat     = cats.find((c: any) => String(c.id) === editItem.category_id);
    const pCat    = productCats.find((c: any) => String(c.id) === editItem.product_category_id);
    const account = cashAccs.find((a: any) => String(a.id) === editItem.account_id);
    const selItem = productItems.find((i: any) => i.value === editItem.product_item_id);
    const res = await journalAPI.updateCogs({
      id: editItem.id, date: editItem.date, recipient_name: editItem.recipient_name,
      category_id: editItem.category_id ? parseInt(editItem.category_id) : null,
      category_name: cat?.name || "",
      product_category_id: editItem.product_category_id ? parseInt(editItem.product_category_id) : null,
      product_category_name: pCat?.name || "",
      product_item_id: selItem ? selItem.id : null,
      product_item_type: selItem ? selItem.type : null,
      currency: editItem.currency, amount: parseFloat(editItem.amount),
      exchange_rate: parseFloat(editItem.exchange_rate || "1"),
      status: editItem.status,
      account_id: editItem.account_id ? parseInt(editItem.account_id) : null,
      account_name: account?.name || editItem.account_name,
      notes: editItem.notes, attachment: editItem.attachment,
      truck_plate: editItem.truck_plate || "", // 🆕
      waybill_number: editItem.waybill_number || "", // 🆕
    });
    if (res.success) { showToast("COGS ویرایش شد"); setEditItem(null); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleSaleFromCogs = async () => {
    if (!showSaleModal || !saleCustomerId) { showToast("مشتری را انتخاب کنید", false); return; }
    setSaleLoading(true);
    try {
      const res = await journalAPI.addSaleFromCogs({
        cogs_id: showSaleModal.id,
        customer_id: parseInt(saleCustomerId),
      });
      if (res.success) {
        showToast(`✅ فروش ${res.serial} — ${res.amount} ${res.currency} ثبت شد`);
        setShowSaleModal(null);
        setSaleCustomerId("");
      } else {
        showToast(res.message || "خطا در ثبت فروش", false);
      }
    } catch { showToast("خطا در اتصال", false); }
    setSaleLoading(false);
  };

  const handleBulkSale = async () => {
    if (!bulkSaleCustomerId) { showToast("مشتری را انتخاب کنید", false); return; }
    if (filtered.length === 0) { showToast("هیچ موردی فیلتر نشده", false); return; }
    setBulkSaleLoading(true);
    try {
      const res = await journalAPI.bulkSaleFromCogs({
        cogs_ids: filtered.map((e: any) => e.id),
        customer_id: parseInt(bulkSaleCustomerId),
      });
      if (res.success) {
        showToast(`✅ ${res.count} فروش ثبت شد` + (res.errors?.length ? ` | ${res.errors.length} خطا` : ""));
        setShowBulkSaleModal(false);
        setBulkSaleCustomerId("");
      } else showToast(res.message || "خطا", false);
    } catch { showToast("خطا در اتصال", false); }
    setBulkSaleLoading(false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف COGS ${serial}؟`)) return;
    const res = await journalAPI.deleteCogs(id);
    if (res.success) { showToast("حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handlePay = async () => {
    if (!payForm.account_id) { showToast("حساب الزامی است", false); return; }
    const account = cashAccs.find((a: any) => String(a.id) === payForm.account_id);
    const res = await journalAPI.payCogs(showPay.id, parseInt(payForm.account_id), account?.name || "");
    if (res.success) { showToast("پرداخت شد"); setShowPay(null); setPayForm({ account_id: "" }); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleAddCat = async () => {
    if (!catForm.name) { showToast("نام الزامی است", false); return; }
    const res = await journalAPI.addExpenseCat({ name: catForm.name, parent_id: catForm.parent_id ? parseInt(catForm.parent_id) : null });
    if (res.success) { showToast("اضافه شد"); setCatForm({ name: "", parent_id: "", editId: "" }); const c = await journalAPI.getExpenseCats(); if (c.success) setCats(c.categories || []); }
    else showToast(res.message || "خطا", false);
  };

  const handleUpdateCat = async () => {
    if (!catForm.name || !catForm.editId) return;
    const res = await journalAPI.updateExpenseCat({
      id: parseInt(catForm.editId),
      name: catForm.name,
      parent_id: catForm.parent_id ? parseInt(catForm.parent_id) : null,
    });
    if (res.success) {
      showToast("ویرایش شد");
      setCatForm({ name: "", parent_id: "", editId: "" });
      const c = await journalAPI.getExpenseCats();
      if (c.success) setCats(c.categories || []);
      const l = await journalAPI.listCogs();
      if (l.success) setList(l.cogs || []);
    }
    else showToast(res.message || "خطا", false);
  };

  const handleDeleteCat = async (id: number) => {
    if (!window.confirm("حذف این دسته‌بندی؟")) return;
    const res = await journalAPI.deleteExpenseCat(id);
    if (res.success) { const c = await journalAPI.getExpenseCats(); if (c.success) setCats(c.categories || []); }
    else showToast(res.message || "خطا", false);
  };

  const filteredBase = applyFilter(list, filter, {
    searchFields: ["serial", "recipient_name", "notes", "category_name"],
    dateField: "date",
    amountField: "amount",
    currencyField: "currency",
    statusField: "status",
    accountField: "account_id",
  });
  const filtered = (selectedCats.length > 0
    ? filteredBase.filter((e: any) => selectedCats.includes(String(e.category_id)))
    : filteredBase
  ).filter((e: any) => {
    const st = e.status || "paid";
    if (statusFilter === "all") return true;
    return st === statusFilter;
  });

  const pendingCount = list.filter((e:any) => (e.status||"paid") === "pending").length;

  const totalUSD = filtered.reduce((s: number, e: any) => s + parseFloat(e.usd_amount || 0), 0);
  const totalByCurrency: Record<string, number> = {};
  filtered.forEach((e: any) => {
    const cur = e.currency || "USD";
    totalByCurrency[cur] = (totalByCurrency[cur] || 0) + parseFloat(e.amount || 0);
  });

  const handlePrint = () => {
    const fmt2 = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
    const rows = filtered.map((e: any, i: number) => `
      <tr style="background:${i%2===0?"#fff":"#f8fafc"}">
        <td style="text-align:center;color:#64748b">${i+1}</td>
        <td style="color:#8b5cf6;font-weight:700;font-family:monospace">${e.serial}</td>
        <td>${e.date}</td>
        <td style="font-weight:600">${e.recipient_name}</td>
        <td style="color:#64748b">${e.category_name||"—"}</td>
        <td style="color:#64748b">${e.product_category_name||"—"}</td>
        <td style="text-align:left;font-weight:700;color:#8b5cf6">${fmt2(e.amount)} ${e.currency}</td>
        <td style="text-align:left;color:#10b981">$${fmt2(e.usd_amount)}</td>
        <td><span style="padding:2px 8px;border-radius:5px;font-size:10px;font-weight:700;background:${e.status==="paid"?"#d1fae5":"#fef3c7"};color:${e.status==="paid"?"#065f46":"#92400e"}">${e.status==="paid"?"پرداخت شده":"در انتظار"}</span></td>
        <td style="color:#64748b;font-size:11px">${e.notes||"—"}</td>
      </tr>`).join("");

    const totalRows = Object.entries(totalByCurrency).map(([cur, amt]) =>
      `<tr style="background:#faf5ff"><td colspan="6" style="text-align:right;font-weight:800;color:#8b5cf6">جمع ${cur}:</td><td colspan="2" style="font-weight:900;color:#8b5cf6;text-align:left">${fmt2(amt)} ${cur}</td><td colspan="2"></td></tr>`
    ).join("") + `<tr style="background:#faf5ff"><td colspan="6" style="text-align:right;font-weight:800;color:#10b981">جمع کل (معادل دالر):</td><td colspan="2" style="font-weight:900;color:#10b981;text-align:left">$${fmt2(totalUSD)}</td><td colspan="2"></td></tr>`;

    const catLabel = selectedCats.length > 0
      ? cats.filter((c: any) => selectedCats.includes(String(c.id))).map((c: any) => c.name).join("، ")
      : "همه دسته‌بندی‌ها";

    const html = `<!DOCTYPE html><html dir="rtl" lang="fa">
<head><meta charset="UTF-8"/><title>گزارش COGS</title>
<link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;600;700;800;900&display=swap" rel="stylesheet">
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  @page{size:A4 landscape;margin:12mm 14mm}
  body{font-family:'Vazirmatn',Tahoma,sans-serif;background:#fff;color:#1e293b;font-size:11px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #8b5cf6;padding-bottom:14px;margin-bottom:16px}
  .title{font-size:18px;font-weight:900;color:#0f172a}
  .subtitle{font-size:11px;color:#64748b;margin-top:3px}
  .meta{text-align:left;font-size:10px;color:#64748b;line-height:1.9}
  .meta strong{color:#1e1b2e;font-weight:700}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px}
  .sbox{border-radius:8px;padding:8px 12px;text-align:center}
  .sval{font-size:15px;font-weight:900;margin-bottom:2px}
  .slabel{font-size:9px;color:#64748b;font-weight:600}
  table{width:100%;border-collapse:collapse}
  thead tr{background:linear-gradient(135deg,#1e293b,#334155)}
  th{padding:8px 10px;text-align:right;font-weight:700;color:#f1f5f9;font-size:10px;white-space:nowrap}
  .even{background:#fff}.odd{background:#f8fafc}
  td{padding:7px 10px;border-bottom:1px solid #f1f5f9;vertical-align:middle;font-size:11px}
  .total-row td{background:#faf5ff;border-top:2px solid #8b5cf680;font-weight:800}
  .footer{margin-top:16px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9.5px;color:#94a3b8}
  .sig{border-top:1px dashed #cbd5e1;padding-top:5px;min-width:150px;text-align:center;font-size:10px;color:#64748b}
  @media print{thead{display:table-header-group}tr{page-break-inside:avoid}}
</style></head><body>
<div class="header">
  <div>
    <div class="title">📦 گزارش هزینه اجناس / COGS</div>
    <div class="subtitle">دسته‌بندی: ${catLabel}</div>
  </div>
  <div class="meta">
    <div>تعداد رکورد: <strong>${filtered.length}</strong></div>
    <div>جمع معادل دالر: <strong style="color:#8b5cf6">$${fmt2(totalUSD)}</strong></div>
    <div>تاریخ چاپ: <strong>${new Date().toLocaleDateString("fa-IR")}</strong></div>
  </div>
</div>
<table>
  <thead><tr><th>#</th><th>شماره سند</th><th>تاریخ</th><th>دریافت‌کننده</th><th>دسته‌بندی</th><th>دسته جنس</th><th>مبلغ</th><th>معادل دالر</th><th>وضعیت</th><th>توضیحات</th></tr></thead>
  <tbody>${rows}${totalRows}</tbody>
</table>
<div class="footer">
  <span>سیستم مالی هامیدی گروپ — گزارش COGS</span>
  <div style="display:flex;gap:40px">
    <div class="sig">امضا مسئول مالی</div>
    <div class="sig">امضا مدیر</div>
  </div>
</div>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const editUsdAmt = editItem ? (parseFloat(editItem.amount || "0") / parseFloat(editItem.exchange_rate || "1")) : 0;

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در COGS... (شماره سند، نام دریافت‌کننده، دسته‌بندی)",
          showDateRange: true,
          showAmountRange: true,
          showCurrency: true,
          showStatus: true,
          statusOptions: [
            { value: "paid", label: "پرداخت شده" },
            { value: "pending", label: "در انتظار" },
          ],
          showCategory: false,
          showAccount: true,
          accountOptions: cashAccs.map((a: any) => ({ value: String(a.id), label: a.name })),
        }}
        values={filter}
        onChange={setFilter}
        resultCount={filtered.length}
        totalCount={list.length}
        T={T}
        dark={dark}
            onExport={() => exportToExcel(filtered.map(e => ({
          "شماره سند": e.serial,
          "تاریخ": e.date,
          "دریافت‌کننده": e.recipient_name,
          "دسته‌بندی": e.category_name || "",
          "دسته جنس": e.product_category_name || "",
          "مبلغ": e.amount,
          "ارز": e.currency,
          "نرخ تبدیل": e.exchange_rate,
          "معادل دالر": e.usd_amount,
          "وضعیت": e.status,
          "حساب": e.account_name || "",
          "توضیحات": e.notes || "",
        })), "COGS")}
      exportLabel="دانلود Excel COGS"
      />
      <div style={{ marginBottom: 12, borderRadius: 12, border: `1px solid ${dark ? "rgba(139,92,246,0.25)" : "#e9d5ff"}`, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6" }}>🗂 فیلتر دسته‌بندی</span>
            {selectedCats.length > 0 && <span style={{ background: "#8b5cf6", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{selectedCats.length} دسته انتخاب شده</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {selectedCats.length > 0 && <button onClick={() => { setSelectedCats([]); setTempCats([]); }} style={{ padding: "5px 12px", borderRadius: 7, border: "1.5px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>× پاک کردن</button>}
            <button onClick={() => { setTempCats([...selectedCats]); setShowCatFilter(v => !v); }} style={{ padding: "5px 14px", borderRadius: 7, border: `1.5px solid #8b5cf6`, background: showCatFilter ? "#8b5cf6" : "transparent", color: showCatFilter ? "#fff" : "#8b5cf6", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
              {showCatFilter ? "▲ بستن" : "▼ انتخاب دسته‌بندی"}
            </button>
          </div>
        </div>

        {showCatFilter && (
          <div style={{ padding: 16, background: dark ? "rgba(0,0,0,0.15)" : "#fff", borderTop: `1px solid ${dark ? "rgba(139,92,246,0.15)" : "#e9d5ff"}` }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button onClick={() => setTempCats(cats.map((c: any) => String(c.id)))} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>انتخاب همه</button>
              <button onClick={() => setTempCats([])} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>هیچکدام</button>
              <span style={{ fontSize: 11, color: T.subtext, alignSelf: "center" }}>{tempCats.length} تیک زده شده</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6, marginBottom: 14 }}>
              {cats.map((c: any) => {
                const checked = tempCats.includes(String(c.id));
                const count = list.filter((e: any) => String(e.category_id) === String(c.id)).length;
                return (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${checked ? "#8b5cf6" : (dark ? "rgba(255,255,255,0.08)" : "#e2e8f0")}`, background: checked ? (dark ? "rgba(139,92,246,0.18)" : "#f5f3ff") : (dark ? "rgba(255,255,255,0.02)" : "#fafafa"), transition: "all 0.15s", userSelect: "none" as const }} title={c.name}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setTempCats(prev => checked ? prev.filter(id => id !== String(c.id)) : [...prev, String(c.id)])}
                      style={{ accentColor: "#8b5cf6", width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ fontSize: 12, fontWeight: checked ? 700 : 500, color: checked ? "#8b5cf6" : T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.parent_id ? <span style={{ color: T.subtext }}>↳ </span> : null}{c.name}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: checked ? "#8b5cf6" : T.subtext, background: checked ? "rgba(139,92,246,0.15)" : (dark ? "rgba(255,255,255,0.06)" : "#f1f5f9"), padding: "1px 7px", borderRadius: 10, flexShrink: 0 }}>{count}</span>
                  </label>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#e9d5ff"}`, paddingTop: 12 }}>
              <button
                onClick={() => { setSelectedCats([...tempCats]); setShowCatFilter(false); }}
                style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "inherit", boxShadow: "0 4px 14px rgba(139,92,246,0.4)", display: "flex", alignItems: "center", gap: 8 }}
              >
                ✓ اعمال فیلتر
                {tempCats.length > 0 && <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 20, padding: "1px 8px", fontSize: 12 }}>{tempCats.length} دسته</span>}
              </button>
            </div>
          </div>
        )}

        {!showCatFilter && selectedCats.length > 0 && (
          <div style={{ padding: "8px 16px", background: dark ? "rgba(0,0,0,0.1)" : "#fff", borderTop: `1px solid ${dark ? "rgba(139,92,246,0.1)" : "#e9d5ff"}`, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {selectedCats.map(id => {
              const cat = cats.find((c: any) => String(c.id) === id);
              return cat ? <span key={id} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: dark ? "rgba(139,92,246,0.2)" : "#ede9fe", color: "#8b5cf6", fontWeight: 600, border: "1px solid rgba(139,92,246,0.3)" }}>{cat.name}</span> : null;
            })}
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div style={{ marginBottom: 12, padding: "10px 16px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.06)" : "#faf5ff", border: `1px solid ${dark ? "rgba(139,92,246,0.15)" : "#e9d5ff"}`, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.subtext }}>🧮 مجموع {filtered.length} رکورد فیلتر شده:</span>
          {Object.entries(totalByCurrency).map(([cur, amt]) => (
            <span key={cur} style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6" }}>{fmt(amt as number)} {cur}</span>
          ))}
          <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>≈ ${fmt(totalUSD)}</span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs
          value={statusFilter}
          onChange={setStatusFilter}
          totalCount={list.length}
          pendingCount={pendingCount}
          T={T}
          dark={dark}
          confirmedValue="paid"
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 10, border: "1.5px solid #10b981", background: "transparent", color: "#10b981", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>🖨️ پرینت گزارش</button>
          {filtered.length > 0 && canAdd && (
            <button onClick={() => { setShowBulkSaleModal(true); setBulkSaleCustomerId(""); }} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 10, border: "1.5px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
              💰 اعمال فروش گروهی ({filtered.length} مورد)
            </button>
          )}
          <button onClick={() => setShowCats(true)} style={{ padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>دسته‌بندی هزینه</button>
          {canAdd && <button onClick={() => setShowForm(true)} style={addBtnSt("#8b5cf6")}><Plus size={15} /> ثبت COGS</button>}
        </div>
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && <Empty T={T} text="هیچ هزینه‌ای ثبت نشده" />}
          {/* ردیف هدر */}
          {filtered.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 1fr 1.2fr 180px 230px",
              gap: 12,
              padding: "10px 16px",
              borderRadius: 10,
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${T.cardBorder}`,
              fontSize: 11,
              fontWeight: 700,
              color: T.subtext,
            }}>
              <div>شماره سند</div>
              <div>موتر / بارنامه</div>
              <div>دریافت‌کننده</div>
              <div>تاریخ</div>
              <div>دسته‌بندی</div>
              <div>شرح</div>
              <div style={{ textAlign: "left" as const }}>مبلغ</div>
              <div style={{ textAlign: "center" as const }}>عملیات</div>
            </div>
          )}
          {filtered.map((e: any) => {
            const isPending = (e.status || "paid") === "pending";
            return (
            <div key={e.id} style={{
              ...rowSt(dark, T),
              ...(isPending ? pendingRowStyle(dark) : {}),
              display: "grid",
              gridTemplateColumns: "130px 220px 1fr 110px 1fr 1.2fr 180px 230px",
              gap: 12,
              alignItems: "center",
            }}>
              {/* ستون 1: سریال + بدج */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                <span style={badgeSt("#8b5cf6")}>{e.serial}</span>
                {isPending && <PendingBadge />}
              </div>

              {/* ستون 2: موتر و بارنامه */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                {e.truck_plate && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    🚚 {e.truck_plate}
                  </span>
                )}
                {e.waybill_number && (
                  <span style={{
                    padding: "3px 10px", borderRadius: 7,
                    background: "rgba(14,165,233,0.1)", color: "#0ea5e9",
                    fontSize: 11, fontWeight: 700, fontFamily: "monospace",
                    whiteSpace: "nowrap" as const,
                  }}>
                    📄 {e.waybill_number}
                  </span>
                )}
              </div>

              {/* ستون 3: نام دریافت‌کننده */}
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>
                {e.recipient_name}
              </div>

              {/* ستون 4: تاریخ */}
              <div>
                <DualDate date={e.date} fontSize={12} color={T.subtext} />
              </div>

              {/* ستون 5: دسته‌بندی */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                {e.category_name && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.06)" : "#f1f5f9", color: T.subtext, whiteSpace: "nowrap" as const }}>{e.category_name}</span>}
                {e.product_category_name && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: dark ? "rgba(139,92,246,0.15)" : "#f5f3ff", color: "#8b5cf6", whiteSpace: "nowrap" as const }}>{e.product_category_name}</span>}
              </div>

              {/* ستون 6: شرح */}
              <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" as const }}>
                {e.notes || "—"}
              </div>

              {/* ستون 7: مبلغ */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                <span style={{ fontWeight: 800, color: "#8b5cf6", fontSize: 14, direction: "ltr" as const }}>{fmt(e.amount)} {e.currency}</span>
                <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const }}>≈ ${fmt(e.usd_amount)}</span>
              </div>

              {/* ستون 8: عملیات */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" as const }}>
                {isPending && canEdit && (
                  <ConfirmIconButton onClick={() => handleConfirm(e)} />
                )}
                {!isPending && canEdit && (
                  <RevertIconButton onClick={() => handleRevert(e.id, e.serial)} />
                )}
                <button onClick={() => { setShowSaleModal(e); setSaleCustomerId(""); }} title="اعمال فروش" style={{ ...addBtnSt("#3b82f6", true), fontSize: 11, padding: "5px 10px" }}>💰 فروش</button>
                {canEdit && <button onClick={() => openEdit(e)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                {canDelete && <button onClick={() => handleDelete(e.id, e.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* فرم ثبت COGS */}
      {showForm && (
        <Modal title="ثبت هزینه اجناس / COGS" onClose={() => setShowForm(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div><Lbl T={T}>نام دریافت‌کننده *</Lbl><input value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} placeholder="نام شخص" style={inputSt(T)} /></div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>دسته‌بندی هزینه</Lbl><SearchableSelect options={catOptions(cats)} value={form.category_id} onChange={v => setForm(f => ({ ...f, category_id: v }))} placeholder="انتخاب دسته‌بندی" /></div>
            <div>
              <Lbl T={T}>محصول یا خدمات مربوطه</Lbl>
              <SearchableSelect
                options={[{ value: "", label: "— بدون انتخاب —" }, ...productItems]}
                value={form.product_item_id}
                onChange={v => {
                  const item = productItems.find(i => i.value === v);
                  setForm(f => ({ ...f, product_item_id: v, product_item_type: item?.type || "" }));
                }}
                placeholder="انتخاب جنس یا خدمت"
              />
            </div>
            <div><Lbl T={T}>ارز هزینه</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={form.currency} onChange={v => setForm(f => ({ ...f, currency: v }))} placeholder="ارز" /></div>
            <div><Lbl T={T}>مبلغ *</Lbl><input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله به دالر</Lbl><input type="number" value={form.exchange_rate} onChange={e => setForm(f => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به دالر</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff", color: "#8b5cf6", fontWeight: 700, fontSize: 14 }}>${fmt(parseFloat(form.amount || "0") / parseFloat(form.exchange_rate || "1"))}</div>
              <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>منطق: {form.amount || 0} ÷ {form.exchange_rate || 1}</div>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Lbl T={T}>حساب پرداختی <span style={{color:"#94a3b8",fontSize:10}}>(برای ثبت نهایی الزامی)</span></Lbl>
              <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={form.account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
            </div>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={form.truck_plate} onChange={e => setForm(f => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={form.waybill_number} onChange={e => setForm(f => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div style={{ gridColumn: "1/-1" }}><Lbl T={T}>ضمیمه</Lbl><input value={form.attachment} onChange={e => setForm(f => ({ ...f, attachment: e.target.value }))} placeholder="لینک یا نام فایل..." style={inputSt(T)} /></div>
          </div>
          <div style={{
            display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center",
            marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`,
            flexWrap: "wrap"
          }}>
            <PendingHint T={T} />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={() => setShowForm(false)} T={T} />
              <PendingSubmitButton
                onClick={() => submit(handleSubmitPending)}
                loading={submitting}
                label="ذخیره COGS در انتظار"
              />
              <ConfirmSubmitButton
                onClick={() => submit(handleSubmit)}
                loading={submitting}
                color="#8b5cf6"
                label="ثبت COGS"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* فرم ویرایش COGS */}
      {editItem && (
        <Modal title="ویرایش COGS" onClose={() => setEditItem(null)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div><Lbl T={T}>نام دریافت‌کننده *</Lbl><input value={editItem.recipient_name} onChange={e => setEditItem((f: any) => ({ ...f, recipient_name: e.target.value }))} placeholder="نام شخص" style={inputSt(T)} /></div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={editItem.date} onChange={v => setEditItem((f: any) => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>دسته‌بندی هزینه</Lbl><SearchableSelect options={catOptions(cats)} value={editItem.category_id} onChange={v => setEditItem((f: any) => ({ ...f, category_id: v }))} placeholder="انتخاب دسته‌بندی" /></div>
            <div>
              <Lbl T={T}>محصول یا خدمات مربوطه</Lbl>
              <SearchableSelect
                options={[{ value: "", label: "— بدون انتخاب —" }, ...productItems]}
                value={editItem.product_item_id}
                onChange={v => {
                  const item = productItems.find((i: any) => i.value === v);
                  setEditItem((f: any) => ({ ...f, product_item_id: v, product_item_type: item?.type || "" }));
                }}
                placeholder="انتخاب جنس یا خدمت"
              />
            </div>
            <div><Lbl T={T}>ارز هزینه</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={editItem.currency} onChange={v => setEditItem((f: any) => ({ ...f, currency: v }))} placeholder="ارز" /></div>
            <div><Lbl T={T}>مبلغ *</Lbl><input type="number" value={editItem.amount} onChange={e => setEditItem((f: any) => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={inputSt(T)} /></div>
            <div><Lbl T={T}>نرخ تبادله به دالر</Lbl><input type="number" value={editItem.exchange_rate} onChange={e => setEditItem((f: any) => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>معادل به دالر</Lbl>
              <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff", color: "#8b5cf6", fontWeight: 700, fontSize: 14 }}>${fmt(editUsdAmt)}</div>
            </div>
            <div><Lbl T={T}>وضعیت پرداخت</Lbl><SearchableSelect options={[{ value: "pending", label: "در انتظار" }, { value: "paid", label: "پرداخت شده" }]} value={editItem.status} onChange={v => setEditItem((f: any) => ({ ...f, status: v, account_id: "", account_name: "" }))} placeholder="وضعیت" /></div>
            {editItem.status === "paid" && (
              <div style={{ gridColumn: "1/-1" }}>
                <Lbl T={T}>حساب پرداختی *</Lbl>
                <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={editItem.account_id}
                  onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setEditItem((f: any) => ({ ...f, account_id: v, account_name: ac?.name || "" })); }} placeholder="انتخاب حساب" />
              </div>
            )}
            <div><Lbl T={T}>توضیحات</Lbl><input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={editItem.truck_plate || ""} onChange={e => setEditItem((f: any) => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={editItem.waybill_number || ""} onChange={e => setEditItem((f: any) => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{...inputSt(T), direction: "ltr" as const}} /></div>
            <div style={{ gridColumn: "1/-1" }}><Lbl T={T}>ضمیمه</Lbl><input value={editItem.attachment} onChange={e => setEditItem((f: any) => ({ ...f, attachment: e.target.value }))} placeholder="لینک..." style={inputSt(T)} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setEditItem(null)} T={T} />
            <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#8b5cf6" label="ذخیره ویرایش" />
          </div>
        </Modal>
      )}

      {/* مودال پرداخت */}
      {showPay && (
        <Modal title={`پرداخت COGS: ${showPay.serial}`} onClose={() => setShowPay(null)} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff" }}>
            <span style={{ fontSize: 13, color: T.text }}>دریافت‌کننده: <strong>{showPay.recipient_name}</strong> | مبلغ: <strong style={{ color: "#8b5cf6" }}>{fmt(showPay.amount)} {showPay.currency}</strong></span>
          </div>
          <Lbl T={T}>حساب پرداختی *</Lbl>
          <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={payForm.account_id} onChange={v => setPayForm({ account_id: v })} placeholder="انتخاب حساب" />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setShowPay(null)} T={T} />
            <BtnSave onClick={() => submit(handlePay)} loading={submitting} color="#10b981" label="تایید پرداخت" />
          </div>
        </Modal>
      )}

      {/* مودال اعمال فروش گروهی */}
      {showBulkSaleModal && (
        <Modal title={`اعمال فروش گروهی — ${filtered.length} مورد`} onClose={() => { setShowBulkSaleModal(false); setBulkSaleCustomerId(""); }} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff", border: "1px solid rgba(59,130,246,0.25)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6", marginBottom: 8 }}>📋 موارد انتخاب شده ({filtered.length} مورد):</div>
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {filtered.map((e: any) => (
                <div key={e.id} style={{ display: "flex", gap: 10, fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}` }}>
                  <span style={{ color: "#8b5cf6", fontWeight: 700, minWidth: 110, flexShrink: 0 }}>{e.serial}</span>
                  <span style={{ color: T.text, flex: 1 }}>{e.recipient_name}</span>
                  <span style={{ color: "#8b5cf6", fontWeight: 700 }}>{fmt(e.amount)} {e.currency}</span>
                  <span style={{ color: "#10b981", fontSize: 11 }}>${fmt(e.usd_amount)}</span>
                  <span style={{ color: T.subtext, fontSize: 11 }}>{e.date}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16, padding: "8px 14px", borderRadius: 8, background: dark ? "rgba(16,185,129,0.06)" : "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", fontSize: 12, color: T.subtext }}>
            ✅ هر مورد به صورت یک فروش جداگانه با همان تاریخ، مبلغ و جزییات در لیست فروشات مشتری ثبت می‌شود.
          </div>
          <Lbl T={T}>انتخاب مشتری *</Lbl>
          <SearchableSelect
            options={customers.map((c: any) => ({
              value: String(c.id),
              label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` — ${c.currency || "USD"}`,
            }))}
            value={bulkSaleCustomerId}
            onChange={v => setBulkSaleCustomerId(v)}
            placeholder="— انتخاب مشتری"
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => { setShowBulkSaleModal(false); setBulkSaleCustomerId(""); }} T={T} />
            <button
              onClick={handleBulkSale}
              disabled={bulkSaleLoading || !bulkSaleCustomerId}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: bulkSaleLoading || !bulkSaleCustomerId ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: bulkSaleLoading || !bulkSaleCustomerId ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}
            >
              {bulkSaleLoading ? "در حال ثبت..." : `💰 ثبت ${filtered.length} فروش`}
            </button>
          </div>
        </Modal>
      )}

      {/* مودال اعمال فروش از COGS */}
      {showSaleModal && (
        <Modal title="اعمال فروش از COGS" onClose={() => { setShowSaleModal(null); setSaleCustomerId(""); }} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.08)" : "#faf5ff", border: "1px solid rgba(139,92,246,0.3)" }}>
            <div style={{ fontSize: 12, color: T.subtext, marginBottom: 6 }}>مشخصات هزینه COGS:</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
              <span><strong style={{ color: "#8b5cf6" }}>{showSaleModal.serial}</strong></span>
              <span style={{ color: T.text }}>{showSaleModal.recipient_name}</span>
              <span style={{ fontWeight: 800, color: "#8b5cf6" }}>{fmt(showSaleModal.amount)} {showSaleModal.currency}</span>
              {showSaleModal.currency !== "USD" && <span style={{ color: "#10b981" }}>≈ ${fmt(showSaleModal.usd_amount)}</span>}
              <DualDate date={showSaleModal.date} fontSize={12} color={T.subtext} />
            </div>
          </div>
          <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff", border: "1px solid rgba(59,130,246,0.2)", fontSize: 12, color: T.subtext }}>
            ℹ️ اگر ارز مشتری با ارز هزینه یکسان باشد: همان مبلغ فروخته می‌شود.<br/>
            اگر ارز مشتری دالر (USD) باشد: معادل دالری (<strong style={{ color: "#10b981" }}>${fmt(showSaleModal.usd_amount)}</strong>) فروخته می‌شود.
          </div>
          <Lbl T={T}>انتخاب مشتری *</Lbl>
          <SearchableSelect
            options={customers.map((c: any) => ({
              value: String(c.id),
              label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` — ${c.currency || "USD"}`,
            }))}
            value={saleCustomerId}
            onChange={v => setSaleCustomerId(v)}
            placeholder="— انتخاب مشتری"
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => { setShowSaleModal(null); setSaleCustomerId(""); }} T={T} />
            <button
              onClick={handleSaleFromCogs}
              disabled={saleLoading || !saleCustomerId}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saleLoading || !saleCustomerId ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: saleLoading || !saleCustomerId ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}
            >
              {saleLoading ? "در حال ثبت..." : "💰 ثبت فروش"}
            </button>
          </div>
        </Modal>
      )}

      {/* مودال دسته‌بندی هزینه */}
      {showCats && (
        <Modal title="مدیریت دسته‌بندی هزینه COGS" onClose={() => setShowCats(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, padding: 16, borderRadius: 12, background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc", border: `1px solid ${T.cardBorder}` }}>
            <div>
              <Lbl T={T}>{catForm.editId ? "ویرایش نام" : "نام دسته‌بندی"} *</Lbl>
              <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="نام" style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>{catForm.editId ? "تغییر دسته مادر" : "زیرمجموعه‌ی"}</Lbl>
              <SearchableSelect
                options={[{ value: "", label: "— دسته اصلی —" }, ...cats.filter((c: any) => !c.parent_id && String(c.id) !== catForm.editId).map((c: any) => ({ value: String(c.id), label: c.name }))]}
                value={catForm.parent_id}
                onChange={v => setCatForm(f => ({ ...f, parent_id: v }))}
                placeholder="دسته مادر"
              />
            </div>
            <div style={{ gridColumn: "1/-1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {catForm.editId && <button onClick={() => setCatForm({ name: "", parent_id: "", editId: "" })} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 12, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>انصراف</button>}
              <button onClick={catForm.editId ? handleUpdateCat : handleAddCat} style={addBtnSt("#8b5cf6", true)}>
                {catForm.editId ? "ذخیره" : <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Plus size={13} /> افزودن</span>}
              </button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {cats.filter((c: any) => !c.parent_id).map((parent: any) => (
              <div key={parent.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", border: `1px solid ${T.cardBorder}` }}>
                  <span style={{ fontWeight: 700, color: T.text, flex: 1, fontSize: 13 }}>{parent.name}</span>
                  {canEdit && <button onClick={() => setCatForm({ name: parent.name, parent_id: "", editId: String(parent.id) })} style={iconBtnSt("#3b82f6")}><Edit2 size={12} /></button>}
                  {canDelete && <button onClick={() => handleDeleteCat(parent.id)} style={iconBtnSt("#ef4444")}><Trash2 size={12} /></button>}
                </div>
                {cats.filter((c: any) => c.parent_id === parent.id).map((child: any) => (
                  <div key={child.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px 8px 32px", borderRadius: 8, marginTop: 4, background: dark ? "rgba(255,255,255,0.02)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                    <span style={{ fontSize: 11, color: T.subtext }}>└</span>
                    <span style={{ color: T.text, flex: 1, fontSize: 13 }}>{child.name}</span>
                    {canEdit && <button onClick={() => setCatForm({ name: child.name, parent_id: String(child.parent_id), editId: String(child.id) })} style={iconBtnSt("#3b82f6")}><Edit2 size={12} /></button>}
                    {canDelete && <button onClick={() => handleDeleteCat(child.id)} style={iconBtnSt("#ef4444")}><Trash2 size={12} /></button>}
                  </div>
                ))}
              </div>
            ))}
            {cats.length === 0 && <Empty T={T} text="هیچ دسته‌بندی ایجاد نشده" />}
          </div>
        </Modal>
      )}
    </div>
  );
}